import React, { HTMLAttributes } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { IRootState } from '../../redux';
import { useIcs } from '../../hooks';
import { IMemoAction } from '../../interfaces';
import {
    cn,
    getMemoActionLokalizeId,
    getCurrentLanguage,
    memoActionDateFormat,
    getMemoActionIcon,
    memoActionStartDateFormat,
} from '../../helpers';
import { MessageDocumentActionType } from '../../enums';
import { IUserState } from '../../redux/user/interfaces';
import { IAppReadoutsState } from '../../redux/appreadouts/interfaces';
import styles from './memoaction.module.scss';
import { Button } from '../Button/Button';
import { CalendarIcon } from '../../assets/images/svg/calendaricon';

interface IMemoActionProps extends HTMLAttributes<HTMLElement> {
    action: IMemoAction;
}

export const MemoAction = ({ action }: IMemoActionProps): JSX.Element => {
    const { download } = useIcs();
    const { languageId } = useSelector<IRootState, IUserState>(({ user }) => user);
    const { supportedLanguages } = useSelector<IRootState, IAppReadoutsState>(({ appReadouts }) => appReadouts);

    const language = getCurrentLanguage(supportedLanguages, languageId).isoCode;
    const Icon = getMemoActionIcon(action.actionCode);

    const handleIcsClick = (): void => {
        download(
            action.reservationStartTime,
            action.reservationEndTime,
            action.reservationAbstract,
            action.description,
            action.reservationUUID,
            action.location,
            action.organizerName,
            action.organizerMail
        );
    };

    return (
        <div className={`d-flex flex-row ${styles.memoaction}`}>
            {action.actionCode === MessageDocumentActionType.Reservation ? (
                <div className="d-flex align-items-center w-100">
                    <div className="col-6 col-md-4 text-truncate" title={action.label}>
                        {action.label}
                    </div>

                    <div className={cn('col-md-4 d-none d-md-flex text-nowrap justify-content-center', styles.time)}>
                        {action.actionStartTime &&
                            memoActionStartDateFormat(new Date(action.actionStartTime), language)}
                    </div>

                    <div className="col-6 col-md-4 text-nowrap d-flex justify-content-md-end">
                        <Button
                            startIcon={<CalendarIcon />}
                            variant="ghost"
                            isRounded
                            onClick={handleIcsClick}
                            style={{ width: 'fit-content' }}
                        >
                            <FormattedMessage id={getMemoActionLokalizeId(action.actionCode)} />
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="d-flex align-items-center w-100">
                    <div className="col-6 col-md-4 text-truncate" title={action.label}>
                        {action.label}
                    </div>

                    <div className={cn('col-md-4 d-none d-md-flex text-nowrap justify-content-center', styles.time)}>
                        {action.actionEndTime && (
                            <>
                                <FormattedMessage id="ACTION_ENDTIME_TEXT" />:{' '}
                                {memoActionDateFormat(new Date(action.actionEndTime), language)}
                            </>
                        )}
                    </div>

                    <div className="col-6 col-md-4 text-nowrap d-flex justify-content-end">
                        <Button
                            as="a"
                            target="_blank"
                            href={action.entryPointUrl}
                            startIcon={<Icon />}
                            variant="ghost"
                            isRounded
                            style={{ width: 'fit-content' }}
                        >
                            <FormattedMessage id={getMemoActionLokalizeId(action.actionCode)} />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
